class MainProduct extends HTMLElement {
  constructor() {
    super();
    this.events = [
      {
        el: this.querySelector(".main-product__buy-buttons cart-add-form"),
        type: "submit",
        callback: this.submitHandler.bind(this),
      },
    ];
  }

  submitHandler(e) {
    e.preventDefault();
    const formData = new FormData(e.target);
    const items = [];
    const item = {};
    for (const [key, value] of formData.entries()) {
      item[key] = value;
    }
    item.product_id = this.dataset.productId;
    items.push(item);

    const data = {
      type: "add",
      source: "product-page",
      items,
      submitter: e.submitter,
    };
    updateCart(data);
  }

  connectedCallback() {
    handleEventListeners(this.events, "add");
    this.variantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.optionValueSelectionChange,
      this.handleVariantChanged.bind(this),
    );
    this.quantityChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.updateQuantity.bind(this),
    );
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      this.updateBuyButtons.bind(this),
    );
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
    this.variantChangeUnsubscriber();
    this.quantityChangeUnsubscriber();
    this.cartUpdateUnsubscriber();
  }

  updateQuantity({ data }) {
    const { quantity, target, section_id, product_id } = data;

    if (
      this.contains(target) ||
      (this.dataset.sectionId === section_id && this.dataset.productId === product_id)
    ) {
      this.querySelector('form [name="quantity"]').value = quantity;
    }
  }

  async handleVariantChanged({ data: { event, target, selectedOptionValues } }) {
    if (!this.contains(event.target)) return;

    // construct url with option values
    const productUrl = this.dataset.url;
    const url = this.getRequestUrl(productUrl, selectedOptionValues);
    const html = await this.fetchProductDOM(url);

    // replace specified DOM nodes
    for (const node of this.nodesToRender) {
      const target = node.id || node.selector;
      if (!target) continue;
      const original_node = this.querySelector(target);
      const new_node = html.querySelector(target);
      if (!original_node || !new_node) continue;
      original_node.parentNode.insertBefore(new_node, original_node);
      original_node.style.display = "none";

      setTimeout(() => original_node.remove(), 500);
    }

    // re-focus changed input
    document.querySelector(`#${target.id}`)?.focus();

    const variant_json = this.querySelector("[data-variant-json]");
    if (!variant_json) return;

    const variant = JSON.parse(variant_json.innerHTML);
    if (variant.featured_image) {
      this.updateVariantMedia(variant.featured_image);
    }

    this.updateHistory(variant.id);
  }

  async updateBuyButtons({ data }) {
    const { source, submitter, product_ids, response } = data;

    if (!product_ids.includes(this.dataset.productId) && response.items.length > 0) return;

    // construct url with option values
    const productUrl = this.dataset.url;
    const url = this.getRequestUrl(productUrl);
    const html = await this.fetchProductDOM(url);

    // replace specified DOM nodes
    for (const node of this.nodesToRender) {
      const target = node.id || node.selector;
      if (!target) continue;
      const original_node = this.querySelector(target);
      const new_node = html.querySelector(target);
      if (!original_node || !new_node) continue;
      original_node.parentNode.insertBefore(new_node, original_node);
      original_node.style.display = "none";

      setTimeout(() => original_node.remove(), 500);
    }
  }

  getRequestUrl(url, optionValues = []) {
    const params = [];

    params.push(`section_id=${this.dataset.sectionId}`);

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(",")}`);
    }

    return `${url}?${params.join("&")}`;
  }

  async fetchProductDOM(url) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const res = await fetch(url, { signal: this.abortController.signal });
      const data = await res.text();
      const html = new DOMParser().parseFromString(data, "text/html");
      return html;
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("Fetch aborted by user");
      } else {
        console.error(error);
      }
    }
  }

  updateVariantMedia(image) {
    this.media_gallery = this.querySelector("media-gallery");
    if (!this.media_gallery) return;

    const gallery_slide = this.media_gallery.querySelector(`[data-media="${image}"]`).dataset
      .swiperSlideIndex;

    this.media_gallery.swiper.slideTo(parseInt(gallery_slide));
  }

  updateHistory(id) {
    if (!history.replaceState) return;
    const url = `${this.dataset.url}${id ? `?variant=${id}` : ""}`;
    window.history.replaceState({}, "", url);
  }

  get nodesToRender() {
    return [
      {
        selector: "variant-select",
      },
      {
        selector: ".main-product__price",
      },
      {
        selector: ".main-product__sku",
      },
      {
        selector: "cart-add-form .btn-elem",
      },
    ];
  }
}

customElements.define("main-product", MainProduct);

class MediaGallery extends HTMLElement {
  constructor() {
    super();

    this.defaultOptions = {
      loop: true,
    };

    this.defaultThumbsOptions = {
      loop: false,
      spaceBetween: 20,
      slidesPerView: 4,
      watchSlidesProgress: true,
      grid: {
        fill: "row",
        rows: 5,
      },
    };

    this.elements = {
      swiper: this.querySelector(".media-gallery"),
      pagination: this.querySelector(".media-gallery__pagination"),
      prevButton: this.querySelector(".media-gallery__button-prev"),
      nextButton: this.querySelector(".media-gallery__button-next"),
      thumbs: this.querySelector(".media-gallery__thumbs"),
    };

    this.swiperOptions = {
      ...this.defaultOptions,
    };
    if (this.elements.swiper.dataset.galleryOptions) {
      this.swiperOptions = {
        ...this.defaultOptions,
        ...JSON.parse(this.elements.swiper.dataset.galleryOptions),
      };
    }

    if (this.elements.prevButton && this.elements.nextButton) {
      this.swiperOptions.navigation = {
        nextEl: this.elements.nextButton,
        prevEl: this.elements.prevButton,
      };
    }

    if (this.elements.pagination) {
      this.swiperOptions.pagination = {
        el: this.elements.pagination,
      };
    }

    if (this.elements.thumbs) {
      this.thumbsSwiperOptions = {
        ...this.defaultThumbsOptions,
      };
      if (this.elements.thumbs.dataset.galleryOptions) {
        this.thumbsSwiperOptions = {
          ...this.defaultThumbsOptions,
          ...JSON.parse(this.elements.thumbs.dataset.galleryOptions),
        };
      }
      this.swiperOptions.thumbs = {
        swiper: this.elements.thumbs,
      };
    }
    this.mediaZoom = this.querySelector("media-zoom");
  }

  connectedCallback() {
    this.addEventListeners();
  }

  addEventListeners() {
    document.addEventListener("DOMContentLoaded", this.init.bind(this));
    this.addEventListener("click", this.clickHandler.bind(this));
  }

  clickHandler(e) {
    if (e.target.classList.contains("media-gallery__play")) {
      this.videoSlide = e.target.closest(".media-gallery__slide-video");
      this.videoSlideMedia = this.videoSlide.querySelector("video");
      this.featuredVideo = document.querySelector("featured-video");
      if (this.videoSlideMedia) {
        this.playMedia(e);
      } else if (this.featuredVideo) {
        this.scrollToVideo();
      }
    }
  }

  init() {
    if (!this.elements.swiper) return;
    if (this.elements.thumbs) {
      this.thumbsSwiper = new Swiper(this.elements.thumbs, this.thumbsSwiperOptions);
    }
    this.swiper = new Swiper(this.elements.swiper, this.swiperOptions);

    this.swiper.on("slideChange", () => {
      const activeSlide = this.swiper.slides[this.swiper.activeIndex];
      if (this.activeVideo) {
        this.activeVideo.pause();
        this.activeVideo.currentTime = 0;
        this.videoSlide.classList.remove("video-playing");
      }
      if (!this.mediaZoom) return;
      if (activeSlide.querySelector(".media-gallery__slide-video")) {
        this.mediaZoom.classList.add("hide-zoom");
      } else {
        this.mediaZoom.classList.remove("hide-zoom");
      }
      const slideMediaURL = new URL(activeSlide.querySelector(".luc-media img").src);
      slideMediaURL.searchParams.set("width", 2560);

      this.mediaZoom.querySelector("[data-zoom-image]").dataset.zoomImage = slideMediaURL;
    });
  }

  playMedia() {
    this.videoSlide.classList.add("video-playing");
    this.activeVideo = this.videoSlideMedia;
    this.videoSlideMedia.play();
  }
  scrollToVideo() {
    const topBar = document.querySelector(".announcement-bar");
    const header = document.querySelector(".site-header");
    this.headerHeight = header?.offsetHeight + topBar?.offsetHeight;
    window.scrollTo({
      top: this.featuredVideo.offsetTop - this.headerHeight,
      behavior: "smooth",
    });
  }
}
customElements.define("media-gallery", MediaGallery);

class FeaturedVideo extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListeners();
  }

  addEventListeners() {
    this.addEventListener("click", this.clickHandler.bind(this));
  }

  clickHandler(e) {
    if (e.target.classList.contains("media-gallery__play")) {
      this.playMedia(e);
    }
  }

  playMedia(e) {
    const video = this.querySelector("video");

    if (!video) return;

    this.querySelector(".featured-video-body").classList.add("video-playing");
    video.play();
  }
}
customElements.define("featured-video", FeaturedVideo);

class MediaZoom extends ModalDialog {
  constructor() {
    super();
    this.x = 50;
    this.y = 50;
    this.s = 100;
    this.cartridge = this.querySelector("[data-zoom-image]");

    this.eventCache = [];
    this.previousDiff = -1;
  }

  addEventListeners() {
    super.addEventListeners();
    this.cartridge.addEventListener("wheel", this.coarseHandler.bind(this));
    this.cartridge.addEventListener("mousedown", this.panStartHandler.bind(this));
    this.cartridge.addEventListener("mouseup", this.panEndHandler.bind(this));
    this.cartridge.addEventListener("mouseleave", this.panEndHandler.bind(this));
    this.cartridge.addEventListener("mousemove", this.panHandler.bind(this));

    this.cartridge.addEventListener("pointerdown", this.pointerDownHandler.bind(this));
    this.cartridge.addEventListener("pointermove", this.pointerMoveHandler.bind(this));
    this.cartridge.addEventListener("pointerup", this.pointerUpHandler.bind(this));
    this.cartridge.addEventListener("pointercancel", this.pointerUpHandler.bind(this));
    this.cartridge.addEventListener("pointerout", this.pointerUpHandler.bind(this));
    this.cartridge.addEventListener("pointerleave", this.pointerUpHandler.bind(this));
  }

  openModal() {
    super.openModal();
    this.cartridgeWidth = this.cartridge.offsetWidth;
    this.cartridgeHeight = this.cartridge.offsetHeight;

    this.loadImage();
  }

  closeModal() {
    super.closeModal();
    this.x = 50;
    this.y = 50;
    this.s = 100;
    this.cartridge.removeAttribute("style");
  }

  clickHandler(e) {
    super.clickHandler(e);
  }

  setZoom(delta) {
    this.s -= delta;
    this.s = Math.min(Math.max(this.s, 100), 200);
    if (this.cartridgeWidth > this.cartridgeHeight) {
      this.cartridge.style.backgroundSize = `auto ${this.s}%`;
    } else if (this.cartridgeWidth < this.cartridgeHeight) {
      this.cartridge.style.backgroundSize = `${this.s}% auto`;
    } else {
      this.cartridge.style.backgroundSize = `${this.s}% ${this.s}%`;
    }
  }

  coarseHandler(e) {
    e.preventDefault();
    if (e.type === "wheel") {
      this.setZoom(e.deltaY);
    }
  }

  panStartHandler(e) {
    if (
      typeof this.y == "undefined" ||
      typeof this.x == "undefined" ||
      typeof this.s == "undefined"
    ) {
      this.x = 50;
      this.y = 50;
      this.s = 100;
    }
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.cartridge.classList.add("panning");
    this.mouseDown = true;
  }

  panEndHandler(e) {
    this.cartridge.classList.remove("panning");
    this.x = this.deltaX;
    this.y = this.deltaY;
    this.mouseDown = false;
  }

  panHandler(e) {
    if (!this.mouseDown) return;
    const deltaX = e.x - this.startX;
    const deltaY = e.y - this.startY;
    const deltaXPercentage = (deltaX / this.cartridgeWidth) * 100;
    const deltaYPercentage = (deltaY / this.cartridgeHeight) * 100;
    this.deltaX = Math.min(Math.max(this.x - deltaXPercentage * (this.s / 100), 0), 100);
    this.deltaY = Math.min(Math.max(this.y - deltaYPercentage * (this.s / 100), 0), 100);
    this.cartridge.style.backgroundPosition = `${this.deltaX}% ${this.deltaY}%`;
  }

  pointerDownHandler(e) {
    this.eventCache.push(e);
    if (this.eventCache.length === 1) {
      this.panStartHandler(e);
    } else {
      this.panEndHandler(e);
    }
  }

  pointerMoveHandler(e) {
    const index = this.eventCache.findIndex((cachedEvent) => cachedEvent.pointerId === e.pointerId);
    this.eventCache[index] = e;

    if (this.eventCache.length === 2) {
      const currentDiff = Math.abs(this.eventCache[0].clientX - this.eventCache[1].clientX);

      if (this.previousDiff > 0) {
        const delta = this.previousDiff - currentDiff;
        this.setZoom(delta);
      }

      this.previousDiff = currentDiff;
    } else {
      this.panHandler(e);
    }
  }

  pointerUpHandler(e) {
    const index = this.eventCache.findIndex((cachedEvent) => cachedEvent.pointerId === e.pointerId);
    this.eventCache.splice(index, 1);
    if (this.eventCache.length < 2) {
      this.previousDiff = -1;
    }
    if (this.eventCache.length <= 0) {
      this.x = this.deltaX;
      this.y = this.deltaY;
      this.mouseDown = false;
    }
  }

  loadImage() {
    this.cartridge.style.backgroundImage = `url(${this.cartridge.dataset.zoomImage})`;
  }
}

customElements.define("media-zoom", MediaZoom);

class ProductDescriptionPreview extends HTMLElement {
  constructor() {
    super();
    this.content = this.querySelector("[data-product-description-content]");
    this.toggle = this.querySelector("[data-product-description-toggle]");
    this.resizeObserver = new ResizeObserver(this.updateTruncation.bind(this));
  }

  connectedCallback() {
    if (!this.content || !this.toggle) return;

    this.toggle.addEventListener("click", this.expand.bind(this));
    this.resizeObserver.observe(this.content);
    this.updateTruncation();
  }

  disconnectedCallback() {
    this.resizeObserver.disconnect();
  }

  updateTruncation() {
    if (this.hasAttribute("expanded") || !this.content) return;

    const isTruncated = this.content.scrollHeight > this.content.clientHeight + 1;
    this.toggle.hidden = !isTruncated;
    this.toggle.setAttribute("aria-expanded", "false");
    this.toggleAttribute("truncated", isTruncated);
  }

  expand() {
    this.toggle.setAttribute("aria-expanded", "true");
    this.toggle.hidden = true;
    this.setAttribute("expanded", "");
    this.removeAttribute("truncated");
    this.resizeObserver.disconnect();
  }
}

customElements.define("product-description-preview", ProductDescriptionPreview);

class ProductTabs extends HTMLElement {
  constructor() {
    super();
    this.tabs = this.querySelectorAll(".main-product__tab");
  }

  addEventListeners() {
    this.addEventListener("click", this.clickHandler.bind(this));
  }

  connectedCallback() {
    this.addEventListeners();
  }

  clickHandler(e) {
    if (!e.target.classList.contains("tab-heading")) return;

    const tab = e.target.closest(".main-product__tab[aria-expanded]");
    if (tab) this.toggleTab(tab);
  }

  toggleTab(tab) {
    const isOpen = tab.getAttribute("aria-expanded") === "true";
    tab.setAttribute("aria-expanded", !isOpen);
  }
}

customElements.define("product-tabs", ProductTabs);
